import * as React from 'react';
import { useFormikContext } from 'formik';
import { ContactCategory } from '../../../../constants';
import { OBContact } from '../../../../types';
import { Tooltip } from '../../../../widgets/Tooltip';

export type ContactFormValues = OBContact & {
  contactCategory: ContactCategory;
};

export const EMPTY_CONTACT: ContactFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  primaryNumber: '',
  primaryNumberType: 'office',
  primaryNumberId: '',
  primaryExtensionNumber: '',
  secondaryNumber: '',
  secondaryNumberType: 'mobile',
  secondaryNumberId: '',
  secondaryExtensionNumber: '',
  role: '',
  isDL: false,
  contactType: 'normal',
  isBasicEscalationContact: false,
  isReportRecipient: false,
  isPortalUser: false,
  isLogSearchUser: false,
  isRiskUser: false,
  isMSP: false,
  isShippingContact: false,
  contactCategory: ContactCategory.USER,
  isManagedAwarenessUser: false,
  isActivateVlcUser: false,
  isExecutiveSponsor: false,
  isMyCyberDashboardUser: false,
};

export const EMPTY_SHIPPING_CONTACT: ContactFormValues = {
  ...EMPTY_CONTACT,
  isShippingContact: true,
  contactCategory: ContactCategory.SC,
};

export const getFormValues = (contact: OBContact): ContactFormValues => ({
  ...contact,
  contactCategory: contact.isDL
    ? ContactCategory.DL
    : contact.isShippingContact
    ? ContactCategory.SC
    : ContactCategory.USER,
});

/**
 * A check to ensure the contact has the reasonable permissions based on the category.
 */
export const cleanPermissions = (
  values: Readonly<ContactFormValues>,
): ContactFormValues => {
  const contact: ContactFormValues = { ...values };

  if (
    contact.contactCategory === ContactCategory.DL ||
    contact.contactCategory === ContactCategory.SC
  ) {
    contact.isPortalUser = false;
    contact.isLogSearchUser = false;
    contact.isRiskUser = false;
    contact.isManagedAwarenessUser = false;
    contact.isActivateVlcUser = false;
    contact.isExecutiveSponsor = false;
    contact.isDL = values.contactCategory === ContactCategory.DL;
  }
  if (contact.contactCategory === ContactCategory.SC) {
    contact.contactType = 'normal';
    contact.isReportRecipient = false;
    contact.isBasicEscalationContact = false;
    contact.isManagedAwarenessUser = false;
    contact.isActivateVlcUser = false;
    contact.isShippingContact = true;
    contact.isExecutiveSponsor = false;
  }

  return contact;
};

export type CategoryOption = {
  label: React.ReactNode;
  value: string;
  customElement?: React.ReactNode;
};

export const CONTACT_CATEGORY_OPTION_MAP: Record<
  ContactCategory,
  CategoryOption
> = {
  user: { value: 'user', label: 'User' },
  dl: {
    value: 'dl',
    label: 'Distribution list',
    customElement: (
      <Tooltip
        text="Distribution lists can't be used for administration."
        placement="bottom"
        className="pl-2"
      />
    ),
  },
  sc: {
    value: 'sc',
    label: 'Shipping',
    customElement: (
      <Tooltip
        text="Shipping contacts can't be used for administration, escalation, or reporting."
        placement="bottom"
        className="pl-2"
      />
    ),
  },
};

export const CONTACT_CATEGORY_OPTIONS_ALL = [
  CONTACT_CATEGORY_OPTION_MAP.user,
  CONTACT_CATEGORY_OPTION_MAP.dl,
  CONTACT_CATEGORY_OPTION_MAP.sc,
];

export const CONTACT_CATEGORY_OPTION_SHIPPING_CONTACT_ONLY = [
  CONTACT_CATEGORY_OPTION_MAP.sc,
];

/**
 * Create a mutable ref object that determines whether the current form requires confirmation
 *  before canceling.
 */
export const useConfirmationRequiredRef = () => {
  const { dirty } = useFormikContext();

  const confirmationRequired = React.useRef(dirty);

  React.useEffect(() => {
    confirmationRequired.current = dirty;
  }, [dirty]);

  return confirmationRequired;
};
